/**
 * Asset management API methods (domains, IPs, IP ranges, URLs, ports).
 */

import apiClient from './client';
import type { Asset, AssetCreateRequest, AssetUpdateRequest, Port, PortCreateRequest } from '@/types/asset';

export type VerificationScope='asset'|'domain';
export interface OwnershipChallenge {verification_method:'dns_txt'|'http_file'|'whois_email';verification_scope?:VerificationScope;domain?:string;verification_token:string|null;token_expires_at:string;record_name?:string;file_url?:string;delivery_queued?:boolean}
export async function startAssetVerification(id:string,method:OwnershipChallenge['verification_method'],scope:VerificationScope='asset'):Promise<OwnershipChallenge>{
  return (await apiClient.post<OwnershipChallenge>(`/v1/assets/${id}/verify/start`,{method,verification_scope:scope})).data;
}
export async function checkAssetVerification(id:string,scope:VerificationScope='asset'):Promise<{verified:boolean}>{
  return (await apiClient.post<{verified:boolean}>(`/v1/assets/${id}/verify/check`,{verification_scope:scope})).data;
}
export async function revokeDomainVerification(id:string):Promise<{revoked:boolean}>{
  return (await apiClient.post<{revoked:boolean}>(`/v1/assets/${id}/verify/revoke-domain`,{})).data;
}

/** List assets for the current tenant. */
export async function getAssets(isActive?: boolean, pagination?: {skip:number;limit:number}): Promise<Asset[]> {
  const params = {...pagination,...(isActive !== undefined ? { is_active: isActive } : {})};
  const { data } = await apiClient.get<Asset[]>('/v1/assets', { params });
  return data;
}

export async function getAssetCount(): Promise<{total:number}> {
  const {data} = await apiClient.get<{total:number}>('/v1/assets/count');
  return data;
}

/** Create a new asset. */
export async function createAsset(payload: AssetCreateRequest): Promise<Asset> {
  const { data } = await apiClient.post<Asset>('/v1/assets', payload);
  return data;
}

/** Get a single asset by ID (includes ports). */
export async function getAsset(assetId: string): Promise<Asset> {
  const { data } = await apiClient.get<Asset>(`/v1/assets/${assetId}`);
  return data;
}

/** Update an asset. */
export async function updateAsset(assetId: string, payload: AssetUpdateRequest): Promise<Asset> {
  const { data } = await apiClient.put<Asset>(`/v1/assets/${assetId}`, payload);
  return data;
}

/** Delete an asset and its associated ports. */
export async function deleteAsset(assetId: string): Promise<void> {
  await apiClient.delete(`/v1/assets/${assetId}`);
}

/** Add a port to an asset. */
export async function addPort(assetId: string, payload: PortCreateRequest): Promise<Port> {
  const { data } = await apiClient.post<Port>(`/v1/assets/${assetId}/ports`, payload);
  return data;
}
